import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";

const S = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 },
  input: { background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 8, color: "#111827", padding: "8px 12px", fontSize: 13, width: "100%" },
  btn: { padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  kpi: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", flex: 1 },
};

const CATEGORIES = ["Fiscal", "Social", "Comptabilité", "Juridique", "RGPD", "TVA", "International", "Autre"];
const URGENCY_COLORS = { haute: "#ef4444", moyenne: "#f59e0b", faible: "#1a9e72" };

function UrgencyBadge({ level }) {
  const c = URGENCY_COLORS[level] || "#4b5563";
  return <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 700, background: c + "22", color: c, textTransform: "capitalize" }}>{level}</span>;
}

// Static fallback deadlines when API returns empty
const STATIC_DEADLINES = [
  { title: "TVA CA3 mensuelle", category: "Fiscal", due_date: (() => { const d = new Date(); d.setMonth(d.getMonth() + 1); d.setDate(24); return d.toISOString().slice(0, 10); })(), urgency: "haute", description: "Déclaration et paiement de la TVA mensuelle." },
  { title: "Déclaration DSN mensuelle", category: "Social", due_date: (() => { const d = new Date(); d.setDate(15); if (d < new Date()) d.setMonth(d.getMonth() + 1); return d.toISOString().slice(0, 10); })(), urgency: "haute", description: "Déclaration Sociale Nominative mensuelle à transmettre à la DSN." },
  { title: "Acompte Impôt sur les Sociétés", category: "Fiscal", due_date: new Date(new Date().getFullYear(), 5, 15).toISOString().slice(0, 10), urgency: "moyenne", description: "2ème acompte IS — 25% de l'IS de l'exercice précédent." },
  { title: "CFE — Cotisation Foncière des Entreprises", category: "Fiscal", due_date: new Date(new Date().getFullYear(), 11, 15).toISOString().slice(0, 10), urgency: "faible", description: "Cotisation foncière des entreprises annuelle." },
  { title: "Déclaration de résultats (2065)", category: "Fiscal", due_date: new Date(new Date().getFullYear(), 3, 30).toISOString().slice(0, 10), urgency: "faible", description: "Dépôt de la déclaration annuelle de résultats IS." },
  { title: "Assemblée Générale annuelle", category: "Juridique", due_date: new Date(new Date().getFullYear(), 5, 30).toISOString().slice(0, 10), urgency: "moyenne", description: "Approbation des comptes annuels N-1 par l'assemblée." },
];

// Plus d'actualités écrites en dur (02/10/2026) : elles étaient présentées
// comme réelles. La liste vient uniquement de l'API (module en préparation).

function daysUntil(dateStr) {
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function VeilleReglementaire({ currentCompanyId, currentSociete }) {
  const { getToken } = useAuth();
  const [tab, setTab] = useState("news");
  const [news, setNews] = useState([]);
  const [deadlines, setDeadlines] = useState(STATIC_DEADLINES);
  const [sources, setSources] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterCat, setFilterCat] = useState("");
  const [filterUrgency, setFilterUrgency] = useState("");
  const [search, setSearch] = useState("");
  const [selectedNews, setSelectedNews] = useState(null);
  const [analysing, setAnalysing] = useState(null);
  const [aiResult, setAiResult] = useState({});
  const [newSourceForm, setNewSourceForm] = useState({ name: "", url: "", feed_url: "", category: "Fiscal" });
  const [addingSource, setAddingSource] = useState(false);
  const [fetching, setFetching] = useState(false);

  const call = useCallback(async (path, method = "GET", body = null) => {
    const res = await fetch("/api/veille" + path, {
      method,
      headers: { Authorization: "Bearer " + getToken(), "Content-Type": "application/json" },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }, [getToken]);

  const loadNews = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (currentCompanyId) qs.set("company_id", currentCompanyId);
      if (filterCat) qs.set("category", filterCat);
      if (filterUrgency) qs.set("urgency", filterUrgency);
      if (search) qs.set("q", search);
      const d = await call(`/news?${qs}`);
      setNews(Array.isArray(d) ? d : []);
    } catch { } finally { setLoading(false); }
  }, [currentCompanyId, filterCat, filterUrgency, search, call]);

  const loadDeadlines = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      if (currentCompanyId) qs.set("company_id", currentCompanyId);
      const d = await call(`/deadlines?${qs}`);
      if (Array.isArray(d) && d.length > 0) setDeadlines(d);
    } catch { }
  }, [currentCompanyId, call]);

  const loadSources = useCallback(async () => {
    try {
      const d = await call("/sources");
      setSources(Array.isArray(d) ? d : []);
    } catch { setSources([]); }
  }, [call]);

  const loadBookmarks = useCallback(async () => {
    try {
      const d = await call("/bookmarks");
      setBookmarks(Array.isArray(d) ? d : []);
    } catch { setBookmarks([]); }
  }, [call]);

  const loadStats = useCallback(async () => {
    try {
      const d = await call("/stats");
      setStats(d);
    } catch { }
  }, [call]);

  useEffect(() => { loadStats(); loadDeadlines(); }, [loadStats, loadDeadlines]);
  useEffect(() => { if (tab === "news") loadNews(); }, [tab, loadNews]);
  useEffect(() => { if (tab === "sources") loadSources(); }, [tab, loadSources]);
  useEffect(() => { if (tab === "bookmarks") loadBookmarks(); }, [tab, loadBookmarks]);

  const markRead = async (id) => {
    try {
      await call(`/news/${id}/read`, "PATCH");
      setNews(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch { }
  };

  const toggleBookmark = async (id) => {
    try {
      await call(`/news/${id}/bookmark`, "PATCH");
      setNews(prev => prev.map(n => n.id === id ? { ...n, is_bookmarked: !n.is_bookmarked } : n));
    } catch { }
  };

  const analyzeArticle = async (id) => {
    setAnalysing(id);
    try {
      const d = await call(`/news/${id}/analyze`, "POST");
      setAiResult(prev => ({ ...prev, [id]: d }));
    } catch (e) {
      // Message de l'API (ex. « module en préparation ») plutôt qu'un renvoi vers une clé API
      let message = "Analyse IA non disponible pour le moment.";
      try { message = JSON.parse(e.message).error || message; } catch { }
      setAiResult(prev => ({ ...prev, [id]: { summary: message, impact: "—", action: "—" } }));
    } finally { setAnalysing(null); }
  };

  const fetchNews = async () => {
    setFetching(true);
    try {
      await call("/fetch", "POST");
      loadNews();
    } catch { } finally { setFetching(false); }
  };

  const addSource = async () => {
    try {
      await call("/sources", "POST", newSourceForm);
      setNewSourceForm({ name: "", url: "", feed_url: "", category: "Fiscal" });
      setAddingSource(false);
      loadSources();
    } catch { }
  };

  const filteredNews = news.filter(n => {
    if (filterCat && n.category !== filterCat) return false;
    if (filterUrgency && n.urgency !== filterUrgency) return false;
    if (search && !n.title?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const upcomingDeadlines = deadlines
    .map(d => ({ ...d, diff: daysUntil(d.due_date) }))
    .filter(d => d.diff > -30)
    .sort((a, b) => a.diff - b.diff);

  const TABS = [
    { id: "news",      label: "📰 Fil d'actualités" },
    { id: "deadlines", label: "📅 Échéances" },
    { id: "ia",        label: "🤖 Analyse IA" },
    { id: "sources",   label: "🔗 Sources" },
    { id: "bookmarks", label: "🔖 Favoris" },
  ];

  return (
    <div style={{ padding: "0 0 40px" }}>
      {/* Collecte et analyse IA désactivées côté serveur (cron/regulatoryFetch.js) */}
      <div role="status" style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 10, fontSize: 13, background: "var(--badge-warning-bg)", color: "var(--badge-warning-text)", border: "1px solid var(--border)" }}>
        <strong>Module en préparation</strong> — la collecte et l'analyse automatiques des actualités réglementaires seront disponibles prochainement.
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827" }}>⚖️ Veille Réglementaire</h2>
          <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{currentSociete} — actualités fiscales, sociales et juridiques</div>
        </div>
        <button onClick={fetchNews} disabled={fetching} style={{ ...S.btn, background: "#185fa5", color: "#fff", opacity: fetching ? 0.6 : 1 }}>
          {fetching ? "Collecte…" : "🔄 Actualiser les sources"}
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        {[
          { label: "Articles non lus", value: String(news.filter(n => !n.is_read).length), color: "#185fa5" },
          { label: "Urgences détectées", value: String(news.filter(n => n.urgency === "haute").length), color: "#ef4444" },
          { label: "Échéances proches", value: String(upcomingDeadlines.filter(d => d.diff <= 30).length), color: "#f59e0b" },
          { label: "Favoris sauvegardés", value: String(news.filter(n => n.is_bookmarked).length), color: "#1a9e72" },
        ].map(k => (
          <div key={k.label} style={{ ...S.kpi, minWidth: 140 }}>
            <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #e5e7eb" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...S.btn, background: "transparent", color: tab === t.id ? "#1a9e72" : "#4b5563", borderBottom: `2px solid ${tab === t.id ? "#1a9e72" : "transparent"}`, borderRadius: 0, padding: "8px 14px 10px", fontSize: 12 }}>{t.label}</button>
        ))}
      </div>

      {/* ── Fil d'actualités ── */}
      {tab === "news" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <input placeholder="🔍 Rechercher un article…" value={search} onChange={e => setSearch(e.target.value)} style={{ ...S.input, maxWidth: 280 }} />
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...S.input, width: 150 }}>
              <option value="">Toutes catégories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filterUrgency} onChange={e => setFilterUrgency(e.target.value)} style={{ ...S.input, width: 130 }}>
              <option value="">Toute urgence</option>
              <option value="haute">Haute</option>
              <option value="moyenne">Moyenne</option>
              <option value="faible">Faible</option>
            </select>
          </div>

          {loading ? <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Chargement…</div> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {filteredNews.map(n => (
                <div key={n.id} style={{ ...S.card, opacity: n.is_read ? 0.75 : 1, borderLeft: `3px solid ${URGENCY_COLORS[n.urgency] || "#4b5563"}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                        <UrgencyBadge level={n.urgency} />
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "rgba(24,95,165,0.2)", color: "#60a5fa" }}>{n.category}</span>
                        {!n.is_read && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "rgba(26,158,114,0.2)", color: "#1a9e72", fontWeight: 700 }}>NOUVEAU</span>}
                      </div>
                      <div style={{ color: "#111827", fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{n.title}</div>
                      <div style={{ color: "#4b5563", fontSize: 12, marginBottom: 8 }}>{n.summary}</div>
                      <div style={{ color: "#4b5563", fontSize: 11 }}>Source : {n.source_name || "—"} · {(n.published_at || "").slice(0, 10)}</div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => toggleBookmark(n.id)} style={{ ...S.btn, padding: "4px 10px", background: n.is_bookmarked ? "rgba(26,158,114,0.2)" : "#f9fafb", color: n.is_bookmarked ? "#1a9e72" : "#4b5563", fontSize: 14 }}>🔖</button>
                      <button onClick={() => { markRead(n.id); setSelectedNews(n.id === selectedNews ? null : n.id); }}
                        style={{ ...S.btn, padding: "4px 10px", background: "rgba(24,95,165,0.2)", color: "#60a5fa", fontSize: 12 }}>
                        {selectedNews === n.id ? "▲ Réduire" : "👁 Lire"}
                      </button>
                    </div>
                  </div>
                  {selectedNews === n.id && (
                    <div style={{ marginTop: 16, padding: 14, background: "#f9fafb", borderRadius: 8 }}>
                      <div style={{ color: "#111827", fontSize: 13 }}>{n.summary}</div>
                      <button onClick={() => analyzeArticle(n.id)} disabled={analysing === n.id} style={{ marginTop: 12, ...S.btn, background: "#534ab7", color: "#fff", fontSize: 12, opacity: analysing === n.id ? 0.6 : 1 }}>
                        {analysing === n.id ? "Analyse IA…" : "🤖 Analyser avec l'IA"}
                      </button>
                      {aiResult[n.id] && (
                        <div style={{ marginTop: 12, padding: 12, background: "rgba(83,74,183,0.1)", borderRadius: 8, border: "1px solid rgba(83,74,183,0.3)" }}>
                          <div style={{ color: "#a78bfa", fontWeight: 700, fontSize: 12, marginBottom: 6 }}>🤖 Analyse IA</div>
                          <div style={{ color: "#111827", fontSize: 12, marginBottom: 4 }}><strong>Résumé :</strong> {aiResult[n.id].summary}</div>
                          {aiResult[n.id].impact && <div style={{ color: "#111827", fontSize: 12, marginBottom: 4 }}><strong>Impact :</strong> {aiResult[n.id].impact}</div>}
                          {aiResult[n.id].action && <div style={{ color: "#1a9e72", fontSize: 12 }}><strong>Action :</strong> {aiResult[n.id].action}</div>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {filteredNews.length === 0 && <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Aucune actualité pour le moment — module de veille en préparation.</div>}
            </div>
          )}
        </div>
      )}

      {/* ── Échéances ── */}
      {tab === "deadlines" && (
        <div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {upcomingDeadlines.map((d, i) => {
              const urgencyColor = d.diff < 7 ? "#ef4444" : d.diff < 30 ? "#f59e0b" : "#1a9e72";
              return (
                <div key={i} style={{ ...S.card, display: "flex", alignItems: "center", gap: 20, padding: "16px 20px", borderLeft: `3px solid ${urgencyColor}` }}>
                  <div style={{ width: 60, height: 60, borderRadius: 12, background: urgencyColor + "22", border: `2px solid ${urgencyColor}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, flexDirection: "column" }}>
                    <span style={{ fontSize: 16, fontWeight: 800, color: urgencyColor, lineHeight: 1 }}>{d.diff < 0 ? "PASSÉ" : d.diff}</span>
                    {d.diff >= 0 && <span style={{ fontSize: 9, color: urgencyColor }}>jours</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#111827", fontWeight: 700, fontSize: 14 }}>{d.title}</div>
                    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                      <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 20, background: "rgba(24,95,165,0.2)", color: "#60a5fa" }}>{d.category}</span>
                      <span style={{ color: "#4b5563", fontSize: 12 }}>Échéance : {d.due_date}</span>
                    </div>
                    {d.description && <div style={{ color: "#4b5563", fontSize: 12, marginTop: 4 }}>{d.description}</div>}
                  </div>
                  <button style={{ ...S.btn, background: "rgba(24,95,165,0.2)", color: "#60a5fa", fontSize: 12, flexShrink: 0 }}>🔔 Rappel</button>
                </div>
              );
            })}
            {upcomingDeadlines.length === 0 && <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Aucune échéance prévue</div>}
          </div>
        </div>
      )}

      {/* ── IA ── */}
      {tab === "ia" && (
        <div>
          <div style={{ background: "rgba(83,74,183,0.1)", border: "1px solid rgba(83,74,183,0.3)", borderRadius: 12, padding: 16, marginBottom: 20 }}>
            <div style={{ color: "#a78bfa", fontWeight: 700, marginBottom: 4 }}>🤖 Analyse IA des actualités réglementaires</div>
            <div style={{ color: "#4b5563", fontSize: 13 }}>L'IA analyse chaque article pour évaluer son impact sur votre activité et vous proposer des actions concrètes.</div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {news.filter(n => n.urgency === "haute").map(n => (
              <div key={n.id} style={S.card}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ color: "#111827", fontWeight: 700, fontSize: 13 }}>{n.title}</div>
                  <button onClick={() => analyzeArticle(n.id)} disabled={analysing === n.id} style={{ ...S.btn, padding: "4px 10px", background: "#534ab7", color: "#fff", fontSize: 11, flexShrink: 0 }}>
                    {analysing === n.id ? "IA…" : "🤖 Analyser"}
                  </button>
                </div>
                {aiResult[n.id] ? (
                  <div style={{ padding: 12, background: "rgba(83,74,183,0.1)", borderRadius: 8, border: "1px solid rgba(83,74,183,0.3)" }}>
                    <div style={{ color: "#111827", fontSize: 12 }}>{aiResult[n.id].summary}</div>
                    {aiResult[n.id].action && <div style={{ color: "#1a9e72", fontSize: 12, marginTop: 6 }}>→ {aiResult[n.id].action}</div>}
                  </div>
                ) : <div style={{ color: "#4b5563", fontSize: 12 }}>Cliquez sur "Analyser" pour obtenir l'évaluation IA.</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Sources ── */}
      {tab === "sources" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button onClick={() => setAddingSource(!addingSource)} style={{ ...S.btn, background: "#1a9e72", color: "#fff" }}>+ Ajouter une source</button>
          </div>

          {addingSource && (
            <div style={{ ...S.card, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Nouvelle source RSS</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                {[{ l: "Nom de la source", k: "name" }, { l: "URL du site", k: "url" }, { l: "URL du flux RSS", k: "feed_url" }].map(f => (
                  <div key={f.k}>
                    <label style={{ display: "block", color: "#4b5563", fontSize: 11, marginBottom: 4 }}>{f.l}</label>
                    <input value={newSourceForm[f.k]} onChange={e => setNewSourceForm(o => ({ ...o, [f.k]: e.target.value }))} style={S.input} />
                  </div>
                ))}
                <div>
                  <label style={{ display: "block", color: "#4b5563", fontSize: 11, marginBottom: 4 }}>Catégorie</label>
                  <select value={newSourceForm.category} onChange={e => setNewSourceForm(o => ({ ...o, category: e.target.value }))} style={S.input}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={addSource} style={{ ...S.btn, background: "#1a9e72", color: "#fff" }}>✅ Ajouter</button>
                <button onClick={() => setAddingSource(false)} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563" }}>Annuler</button>
              </div>
            </div>
          )}

          <div style={{ ...S.card }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 12 }}>Sources prédéfinies</div>
            {[
              { name: "BOFIP — Bulletin Officiel des Finances Publiques", category: "Fiscal", active: true, icon: "🏛️" },
              { name: "Légifrance — Journal Officiel", category: "Juridique", active: true, icon: "⚖️" },
              { name: "CNIL — Commission Nationale Informatique et Libertés", category: "RGPD", active: true, icon: "🔒" },
              { name: "URSSAF — Actualités sociales", category: "Social", active: true, icon: "👥" },
              { name: "DGFiP — Direction Générale des Finances Publiques", category: "Fiscal", active: false, icon: "📊" },
              { name: "MEDEF — Actualités entreprises", category: "Autre", active: false, icon: "🏢" },
            ].concat(sources.map(s => ({ name: s.name, category: s.category, active: !!s.is_active, icon: "🔗", custom: true }))).map((src, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                <span style={{ fontSize: 18 }}>{src.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#111827", fontSize: 13, fontWeight: 600 }}>{src.name}</div>
                  <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 20, background: "rgba(24,95,165,0.2)", color: "#60a5fa" }}>{src.category}</span>
                </div>
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 700, background: src.active ? "rgba(26,158,114,0.2)" : "rgba(100,116,139,0.2)", color: src.active ? "#1a9e72" : "#4b5563" }}>
                  {src.active ? "Actif" : "Inactif"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Bookmarks ── */}
      {tab === "bookmarks" && (
        <div>
          {news.filter(n => n.is_bookmarked).concat(bookmarks).length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "#4b5563" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🔖</div>
              <div>Aucun favori sauvegardé. Cliquez sur l'icône 🔖 sur un article pour le sauvegarder.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {news.filter(n => n.is_bookmarked).map(n => (
                <div key={n.id} style={{ ...S.card }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                    <UrgencyBadge level={n.urgency} />
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "rgba(24,95,165,0.2)", color: "#60a5fa" }}>{n.category}</span>
                  </div>
                  <div style={{ color: "#111827", fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{n.title}</div>
                  <div style={{ color: "#4b5563", fontSize: 12 }}>{n.summary}</div>
                  <div style={{ color: "#4b5563", fontSize: 11, marginTop: 8 }}>{n.source_name} · {(n.published_at || "").slice(0, 10)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
